import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  useMediaQuery,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { ValidationError } from "yup";

import { Bubble } from "../../components/Icons/Bubble";
import { LinearGradient } from "../../components/Icons/LinearGradient";
import Navigation from "../../components/Navigation";
import { loginUser } from "../../api/api";
import { loadSessionToken } from "../../store/localStorage";
import { LoginFormValues, useValidation } from "./form";
import { AlertProps } from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import { Alert } from "@mui/material";

import theme from "../../components/styles/theme/theme";

const Login: React.FC = () => {
  const { t } = useTranslation();
  const isSmScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [snackbar, setSnackbar] = React.useState<Pick<
    AlertProps,
    "children" | "severity"
  > | null>(null);

  const handleCloseSnackbar = () => setSnackbar(null);

  const initialErrorsState: LoginFormValues = {
    email: "",
    password: "",
  };
  const [errors, setErrors] = useState<LoginFormValues>(initialErrorsState);
  const session = loadSessionToken();

  const validationSchema = useValidation(t);

  if (!!session) {
    navigate("/");
    return null;
  }

  const validateForm = async () => {
    try {
      await validationSchema.validate(
        { email, password },
        { abortEarly: false }
      );
      setErrors(initialErrorsState);
      return true;
    } catch (error) {
      if (error instanceof ValidationError) {
        const newErrors = { ...initialErrorsState };

        error.inner.forEach((e) => {
          if (e.path === "email" || e.path === "password") {
            newErrors[e.path] = e.message;
          }
        });

        setErrors(newErrors);
        return false;
      }
    }
  };

  const handleLogin = async () => {
    const isValid = await validateForm();

    if (isValid) {
      const loggedIn = await loginUser(email, password);
      if (loggedIn) {
        navigate("/");
        return true;
      }
      setSnackbar({
        children:
          "Something went wrong during the login process. Please try again.",
        severity: "error",
      });
    }
  };

  return (
    <Box>
      <Navigation isMenuHidden />
      <Box
        height="90vh"
        display="flex"
        justifyContent="center"
        alignItems="center"
        sx={{ overflow: "hidden", width: "100%" }}
      >
        <Paper elevation={3} sx={{ padding: "20px" }}>
          <Typography
            variant="h5"
            align="center"
            color={theme.palette.secondary.main}
            gutterBottom
          >
            {t("common.login")}
          </Typography>
          <form onSubmit={(e) => e.preventDefault()}>
            <TextField
              label={t("common.email")}
              variant="outlined"
              fullWidth
              margin="normal"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={!!errors.email}
              helperText={errors.email}
            />
            <TextField
              label={t("common.password")}
              variant="outlined"
              fullWidth
              margin="normal"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={!!errors.password}
              helperText={errors.password}
            />
            <Button
              onClick={handleLogin}
              fullWidth
              sx={{
                padding: "8px 16px",
                marginTop: "20px",
                backgroundColor: "secondary.main",
                ":hover": {
                  backgroundColor: "secondary.dark",
                },
                color: theme.palette.common.white,
              }}
              size="small"
            >
              {t("common.login")}
            </Button>
          </form>
        </Paper>
      </Box>
      {!isSmScreen && (
        <Bubble
          style={{
            position: "absolute",
            left: "115px",
            top: "10vh",
            zIndex: -1,
          }}
        />
      )}
      <LinearGradient
        style={{
          width: "100%",
          position: "absolute",
          bottom: "5vh",
          zIndex: -1,
        }}
      />
      {!!snackbar && (
        <Snackbar
          open
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          onClose={handleCloseSnackbar}
          autoHideDuration={6000}
        >
          <Alert {...snackbar} onClose={handleCloseSnackbar} />
        </Snackbar>
      )}
    </Box>
  );
};

export default Login;
